import {yupResolver} from "@hookform/resolvers/yup";
import * as Sentry from "@sentry/react";
import {Frequency, Options, RRule, Weekday as RRuleWeekday} from "rrule";

import {dynamicTabConfig, staticTabConfig} from "@/components/layout/TabContentContainer.types.ts";
import {MaintenanceScheduleEditData, MaintenanceScheduleType} from "@/lib/types/maintenance.ts";
import {
  MaintenanceScheduleFormData,
  MaintenanceScheduleTabs,
} from "@/pages/maintenance/maintenance-create/CreateEditMaintenanceScheduleForm.types.ts";
import {
  MaintenanceScheduleAffectedChecksDefaultValues,
  MaintenanceScheduleAffectedChecksSchema,
} from "@/pages/maintenance/maintenance-create/tabs/affected-checks/MaintenanceScheduleAffectedChecksTab.form.ts";
import {MaintenanceScheduleAffectedChecksTab} from "@/pages/maintenance/maintenance-create/tabs/affected-checks/MaintenanceScheduleAffectedChecksTab.tsx";
import {
  MaintenanceScheduleGeneralDefaultValues,
  MaintenanceScheduleGeneralSchema,
} from "@/pages/maintenance/maintenance-create/tabs/general/MaintenanceScheduleGeneralTab.form.ts";
import {MaintenanceScheduleGeneralTab} from "@/pages/maintenance/maintenance-create/tabs/general/MaintenanceScheduleGeneralTab.tsx";
import {
  CustomRepeatType,
  DurationUnit,
  EndType,
  FrequencyType,
  MonthlyRepeatMode,
  Weekday,
} from "@/pages/maintenance/maintenance-create/tabs/general/MaintenanceScheduleGeneralTab.types.ts";
import {getMonthlyRecurrenceParts} from "@/pages/maintenance/maintenance-create/tabs/general/subComponents/MonthlyRepeatOptions/MonthlyRepeatOptions.utils.ts";
import {
  MaintenanceScheduleNotificationsDefaultValues,
  MaintenanceScheduleNotificationsSchema,
} from "@/pages/maintenance/maintenance-create/tabs/notifications/MaintenanceScheduleNotificationsTab.form.ts";
import {MaintenanceScheduleNotificationsTab} from "@/pages/maintenance/maintenance-create/tabs/notifications/MaintenanceScheduleNotificationsTab.tsx";
import {TimingUnit} from "@/pages/maintenance/maintenance-create/tabs/notifications/NotificationRow.types.ts";
import {
  offsetToTiming,
  timingToOffset,
} from "@/pages/maintenance/maintenance-create/tabs/notifications/NotificationRow.utils.ts";
import {mergeSchemas} from "@/pages/statuspages/statuspages-create/CreateStatusPageForm.utils.tsx";

export const getStaticMaintenanceScheduleTabConfig = (): Record<MaintenanceScheduleTabs, staticTabConfig> => ({
  [MaintenanceScheduleTabs.GENERAL]: {
    id: MaintenanceScheduleTabs.GENERAL,
    title: "General",
    fieldList: Object.keys(MaintenanceScheduleGeneralSchema.describe().fields),
    html: () => <MaintenanceScheduleGeneralTab />,
  },
  [MaintenanceScheduleTabs.AFFECTED_CHECKS]: {
    id: MaintenanceScheduleTabs.AFFECTED_CHECKS,
    title: "Affected Checks",
    fieldList: Object.keys(MaintenanceScheduleAffectedChecksSchema.describe().fields),
    html: () => <MaintenanceScheduleAffectedChecksTab />,
  },
  [MaintenanceScheduleTabs.NOTIFICATIONS]: {
    id: MaintenanceScheduleTabs.NOTIFICATIONS,
    title: "Notifications",
    fieldList: Object.keys(MaintenanceScheduleNotificationsSchema.describe().fields),
    html: () => <MaintenanceScheduleNotificationsTab />,
  },
});

const StaticMaintenanceScheduleTabConfig = getStaticMaintenanceScheduleTabConfig();

export const MaintenanceScheduleFieldToTabMap: Record<string, MaintenanceScheduleTabs> = Object.values(
  StaticMaintenanceScheduleTabConfig
).reduce((acc: Record<string, MaintenanceScheduleTabs>, section) => {
  if (section.fieldList) {
    section.fieldList.forEach((field: string) => {
      acc[field] = section.id as MaintenanceScheduleTabs;
    });
  }
  return acc;
}, {});

export const MaintenanceScheduleFieldList = Object.keys(MaintenanceScheduleFieldToTabMap);

export const MaintenanceScheduleFormPrevalidationFields: string[] = [];

export const defaultMaintenanceScheduleConfig: Record<MaintenanceScheduleTabs, dynamicTabConfig> = {
  [MaintenanceScheduleTabs.GENERAL]: {
    isVisible: true,
  },
  [MaintenanceScheduleTabs.AFFECTED_CHECKS]: {
    isVisible: true,
  },
  [MaintenanceScheduleTabs.NOTIFICATIONS]: {
    isVisible: true,
  },
};

export const MaintenanceScheduleYupResolver = yupResolver(
  mergeSchemas<MaintenanceScheduleFormData>(
    MaintenanceScheduleGeneralSchema,
    MaintenanceScheduleAffectedChecksSchema,
    MaintenanceScheduleNotificationsSchema
  )
);

export const MaintenanceScheduleFormDefaultValues: MaintenanceScheduleFormData = {
  ...MaintenanceScheduleGeneralDefaultValues,
  ...MaintenanceScheduleAffectedChecksDefaultValues,
  ...MaintenanceScheduleNotificationsDefaultValues,
};

const WEEKDAY_TO_RRULE: Record<string, RRuleWeekday> = {
  [Weekday.SUNDAY]: RRule.SU,
  [Weekday.MONDAY]: RRule.MO,
  [Weekday.TUESDAY]: RRule.TU,
  [Weekday.WEDNESDAY]: RRule.WE,
  [Weekday.THURSDAY]: RRule.TH,
  [Weekday.FRIDAY]: RRule.FR,
  [Weekday.SATURDAY]: RRule.SA,
};

const RRULE_WEEKDAY_NUM_TO_FORM: Record<number, Weekday> = {
  0: Weekday.MONDAY,
  1: Weekday.TUESDAY,
  2: Weekday.WEDNESDAY,
  3: Weekday.THURSDAY,
  4: Weekday.FRIDAY,
  5: Weekday.SATURDAY,
  6: Weekday.SUNDAY,
};

const JS_DAY_TO_RRULE: RRuleWeekday[] = [RRule.SU, RRule.MO, RRule.TU, RRule.WE, RRule.TH, RRule.FR, RRule.SA];

interface ParsedRRuleFormData {
  frequency_type: FrequencyType;
  custom_repeat_type?: CustomRepeatType;
  monthly_repeat_mode?: MonthlyRepeatMode;
  end_type: EndType;
  rrule_interval?: number;
  rrule_byweekday?: string[];
  rrule_bymonthday?: number[];
  rrule_count?: number | null;
  rrule_until?: Date | null;
}

const parseRRule = (rruleString: string | undefined): ParsedRRuleFormData | undefined => {
  if (!rruleString) {
    return undefined;
  }

  const rrule = RRule.fromString(rruleString);
  const {freq, interval, count, until, byweekday, bymonthday, bysetpos} = rrule.options;
  const origByweekday = rrule.origOptions.byweekday;

  const isCustom = interval > 1;
  let frequencyType: FrequencyType;
  let customRepeatType: CustomRepeatType | undefined;

  if (isCustom) {
    frequencyType = FrequencyType.CUSTOM;
    switch (freq) {
      case Frequency.WEEKLY:
        customRepeatType = CustomRepeatType.WEEK;
        break;
      case Frequency.MONTHLY:
        customRepeatType = CustomRepeatType.MONTH;
        break;
      case Frequency.DAILY:
      default:
        customRepeatType = CustomRepeatType.DAY;
        break;
    }
  } else {
    switch (freq) {
      case Frequency.WEEKLY:
        frequencyType = FrequencyType.WEEKLY;
        break;
      case Frequency.MONTHLY:
        frequencyType = FrequencyType.MONTHLY;
        break;
      case Frequency.DAILY:
        frequencyType = FrequencyType.DAILY;
        break;
      default:
        frequencyType = FrequencyType.CUSTOM;
        customRepeatType = CustomRepeatType.DAY;
        break;
    }
  }

  let formWeekdays: string[] | undefined;
  if (freq === Frequency.WEEKLY && byweekday.length > 0) {
    formWeekdays = byweekday.map((dayNum) => RRULE_WEEKDAY_NUM_TO_FORM[dayNum]).filter(Boolean);
  } else if (freq === Frequency.WEEKLY && byweekday.length === 0) {
    Sentry.captureException(new Error("Maintenance with Weekly frequency with no byweekday specified in RRule"));
  }

  let monthlyRepeatMode: MonthlyRepeatMode | undefined;
  let formBymonthday: number[] | undefined;
  if (freq === Frequency.MONTHLY) {
    const hasNthWeekday =
      origByweekday != null &&
      (Array.isArray(origByweekday)
        ? origByweekday.some((weekday) => weekday instanceof RRuleWeekday && weekday.n != null)
        : origByweekday instanceof RRuleWeekday && origByweekday.n != null);

    monthlyRepeatMode = hasNthWeekday ? MonthlyRepeatMode.NTH_WEEKDAY : MonthlyRepeatMode.DAY_OF_MONTH;

    if (
      monthlyRepeatMode === MonthlyRepeatMode.DAY_OF_MONTH &&
      Array.isArray(bysetpos) &&
      bysetpos.includes(-1) &&
      bymonthday.length > 0
    ) {
      formBymonthday = [Math.max(...bymonthday)];
    } else if (bymonthday.length > 0) {
      formBymonthday = bymonthday;
    }
  }

  let endType = EndType.NEVER;
  if (count) {
    endType = EndType.AFTER;
  } else if (until) {
    endType = EndType.ON;
  }

  return {
    frequency_type: frequencyType,
    custom_repeat_type: customRepeatType,
    monthly_repeat_mode: monthlyRepeatMode,
    end_type: endType,
    rrule_interval: interval,
    rrule_byweekday: formWeekdays,
    rrule_bymonthday: formBymonthday,
    rrule_count: count ?? null,
    rrule_until: until ?? null,
  };
};

export const buildRRuleString = (formData: MaintenanceScheduleFormData, timezone?: string): string => {
  if (formData.frequency_type === FrequencyType.DONT_REPEAT) {
    return "";
  }

  if (formData.start_date === undefined) {
    throw new Error("Start date is required for repeating schedules.");
  }

  const options: Partial<Options> = {
    dtstart: formData.start_date,
    interval: 1,
  };

  let resolvedFreq: Frequency;
  if (formData.frequency_type === FrequencyType.CUSTOM) {
    options.interval = formData.rrule_interval ?? 1;
    switch (formData.custom_repeat_type) {
      case CustomRepeatType.WEEK:
        resolvedFreq = Frequency.WEEKLY;
        break;
      case CustomRepeatType.MONTH:
        resolvedFreq = Frequency.MONTHLY;
        break;
      case CustomRepeatType.DAY:
      default:
        resolvedFreq = Frequency.DAILY;
        break;
    }
  } else {
    switch (formData.frequency_type) {
      case FrequencyType.WEEKLY:
        resolvedFreq = Frequency.WEEKLY;
        break;
      case FrequencyType.MONTHLY:
        resolvedFreq = Frequency.MONTHLY;
        break;
      case FrequencyType.DAILY:
      default:
        resolvedFreq = Frequency.DAILY;
        break;
    }
  }

  options.freq = resolvedFreq;

  if (resolvedFreq === Frequency.WEEKLY && formData.rrule_byweekday && formData.rrule_byweekday.length > 0) {
    options.byweekday = formData.rrule_byweekday.map((day) => WEEKDAY_TO_RRULE[day]).filter(Boolean);
  }

  if (resolvedFreq === Frequency.MONTHLY) {
    const {dayOfMonth, jsWeekday, occurrence, isLast} = getMonthlyRecurrenceParts(formData.start_date, timezone);
    if (formData.monthly_repeat_mode === MonthlyRepeatMode.NTH_WEEKDAY) {
      const rruleWeekday = JS_DAY_TO_RRULE[jsWeekday];
      const nth = isLast ? -1 : occurrence;
      options.byweekday = [rruleWeekday.nth(nth)];
    } else {
      if (dayOfMonth >= 29) {
        options.bymonthday = Array.from({length: dayOfMonth - 28 + 1}, (_, i) => 28 + i);
        options.bysetpos = [-1];
      } else {
        options.bymonthday = [dayOfMonth];
      }
    }
  }

  switch (formData.end_type) {
    case EndType.ON:
      if (formData.rrule_until) {
        options.until = formData.rrule_until;
      }
      break;
    case EndType.AFTER:
      if (formData.rrule_count) {
        options.count = formData.rrule_count;
      }
      break;
  }

  const rule = new RRule(options);

  return rule.toString();
};

export const scheduleToFormData = (schedule: MaintenanceScheduleEditData): MaintenanceScheduleFormData => {
  const durationMinutes = schedule.duration_minutes || 60;

  let durationValue: number;
  let durationUnit: DurationUnit;

  if (durationMinutes % (24 * 60) === 0) {
    durationValue = durationMinutes / (24 * 60);
    durationUnit = DurationUnit.DAYS;
  } else if (durationMinutes % 60 === 0) {
    durationValue = durationMinutes / 60;
    durationUnit = DurationUnit.HOURS;
  } else {
    durationValue = durationMinutes;
    durationUnit = DurationUnit.MINUTES;
  }

  const rruleData = parseRRule(schedule.rrule);

  return {
    name: schedule.name,
    pause_checks_during_maintenance: schedule.pause_checks_during_maintenance,
    start_date: schedule.starts_at ? new Date(schedule.starts_at) : undefined,
    end_date: schedule.ends_at ? new Date(schedule.ends_at) : null,
    duration_value: durationValue,
    duration_unit: durationUnit,
    frequency_type: rruleData?.frequency_type ?? FrequencyType.DONT_REPEAT,
    custom_repeat_type: rruleData?.custom_repeat_type,
    monthly_repeat_mode: rruleData?.monthly_repeat_mode,
    end_type: rruleData?.end_type ?? EndType.NEVER,
    rrule_interval: rruleData?.rrule_interval,
    rrule_byweekday: rruleData?.rrule_byweekday,
    rrule_bymonthday: rruleData?.rrule_bymonthday,
    rrule_count: rruleData?.rrule_count,
    rrule_until: rruleData?.rrule_until,
    tags: schedule.tags.map((tag) => tag.id),
    affected_checks: schedule.services.map((service) => ({id: service.id})),
    notifications:
      schedule.notifications && schedule.notifications.length > 0
        ? schedule.notifications.map((n) => {
            const timing = offsetToTiming(n.offset);
            return {
              id: n.id,
              offset: n.offset,
              timing_amount: timing.amount,
              timing_unit: timing.unit,
              event: n.event,
              contact_group_ids: n.contact_groups.map((cg) => cg.id),
            };
          })
        : [
            {
              offset: 0,
              timing_amount: null,
              timing_unit: TimingUnit.MINUTES_BEFORE,
              event: "START",
              contact_group_ids: [],
            },
          ],
  };
};

export const maintenanceScheduleFormDataProcessing = (formData: MaintenanceScheduleFormData, timezone?: string) => {
  let durationMinutes = formData.duration_value;
  switch (formData.duration_unit) {
    case DurationUnit.HOURS:
      durationMinutes = formData.duration_value * 60;
      break;
    case DurationUnit.DAYS:
      durationMinutes = formData.duration_value * 24 * 60;
      break;
  }

  const endsAt =
    formData.frequency_type === FrequencyType.DONT_REPEAT && formData.start_date
      ? new Date(formData.start_date.getTime() + durationMinutes * 60 * 1000)
      : null;

  const rruleString = buildRRuleString(formData, timezone);

  const scheduleType =
    formData.frequency_type === FrequencyType.DONT_REPEAT
      ? MaintenanceScheduleType.ONE_OFF
      : MaintenanceScheduleType.RRULE;

  return {
    name: formData.name,
    schedule_type: scheduleType,
    services: formData.affected_checks.map((check) => check.id),
    is_active: true,
    pause_checks_during_maintenance: formData.pause_checks_during_maintenance,
    starts_at: formData.start_date,
    ends_at: endsAt,
    rrule: rruleString,
    duration_minutes: durationMinutes,
    tags: formData.tags,
    notifications: formData.notifications
      .filter((n) => n.timing_amount !== null)
      .map((n) => ({
        id: n.id,
        offset: timingToOffset(n.timing_amount as number, n.timing_unit),
        event: n.event,
        contact_groups: n.contact_group_ids,
      })),
  };
};
